import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ComparisonPanel } from "./ComparisonPanel";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  FileSearch,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  RefreshCw,
  RotateCcw,
  Target,
  Upload,
  FileText,
  X,
  BarChart3,
  User,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Briefcase,
  GraduationCap,
  Award,
  Languages,
  Wrench,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ResumeStructure {
  contato?: {
    nome?: string;
    email?: string;
    telefone?: string;
    localizacao?: string;
    linkedin?: string;
  };
  resumo_profissional?: string;
  experiencias?: Array<{
    cargo?: string;
    empresa?: string;
    periodo?: string;
    destaques?: string[];
  }>;
  formacao?: Array<{ curso?: string; instituicao?: string; periodo?: string }>;
  habilidades_tecnicas?: string[];
  idiomas?: string[];
  certificacoes?: string[];
}

interface AnalysisResult {
  score: number;
  nivel: "Baixo" | "Médio" | "Alto";
  pontos_fortes: string[];
  lacunas: string[];
  sugestoes: string[];
  resumo: string;
  estrutura?: ResumeStructure;
}


type ViewState = "idle" | "loading" | "result";
type AnalysisMode = "quality" | "comparison";

interface ResumeAnalyzerViewProps {
  onBack: () => void;
}

async function extractTextFromPDF(file: File): Promise<string> {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString();

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pageTexts: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    // Group items by Y coordinate (line)
    const lines: { y: number; parts: { x: number; str: string }[] }[] = [];
    const TOLERANCE = 2;

    for (const raw of content.items as any[]) {
      const str = raw.str ?? "";
      if (!str.trim() && !raw.hasEOL) continue;
      const y = raw.transform?.[5] ?? 0;
      const x = raw.transform?.[4] ?? 0;

      let line = lines.find((l) => Math.abs(l.y - y) < TOLERANCE);
      if (!line) {
        line = { y, parts: [] };
        lines.push(line);
      }
      line.parts.push({ x, str });
    }

    // Sort lines top-to-bottom (higher Y first), parts left-to-right
    lines.sort((a, b) => b.y - a.y);
    const rendered: string[] = [];
    let prevY: number | null = null;

    for (const line of lines) {
      line.parts.sort((a, b) => a.x - b.x);
      const text = line.parts
        .map((p) => p.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (!text) continue;

      // Insert blank line when vertical gap is large (new block)
      if (prevY !== null && prevY - line.y > 18) rendered.push("");
      rendered.push(text);
      prevY = line.y;
    }

    pageTexts.push(rendered.join("\n"));
  }

  return normalizeExtractedText(pageTexts.join("\n\n"));
}

async function extractTextFromDocx(file: File): Promise<string> {
  const mammoth = await import("mammoth/mammoth.browser");
  const arrayBuffer = await file.arrayBuffer();
  const result = await (mammoth as any).extractRawText({ arrayBuffer });
  return normalizeExtractedText((result?.value || "").trim());
}

function normalizeExtractedText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function extractTextFromFile(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (ext === "txt" || ext === "md" || ext === "rtf") {
    return normalizeExtractedText(await file.text());
  }
  if (ext === "pdf") return await extractTextFromPDF(file);
  if (ext === "docx") return await extractTextFromDocx(file);
  if (ext === "doc") {
    throw new Error("Formato .doc antigo não suportado. Salve como .docx ou PDF.");
  }
  throw new Error("Formato não suportado. Use PDF, DOCX ou TXT.");
}

function StructuredResumePanel({ data }: { data: ResumeStructure }) {
  const contato = data.contato || {};
  const hasContato = Object.values(contato).some((v) => v && String(v).trim());
  const experiencias = data.experiencias?.filter((e) => e?.cargo || e?.empresa) || [];
  const formacao = data.formacao?.filter((f) => f?.curso || f?.instituicao) || [];
  const skills = data.habilidades_tecnicas?.filter(Boolean) || [];
  const idiomas = data.idiomas?.filter(Boolean) || [];
  const certs = data.certificacoes?.filter(Boolean) || [];

  if (
    !hasContato &&
    !data.resumo_profissional &&
    experiencias.length === 0 &&
    formacao.length === 0 &&
    skills.length === 0 &&
    idiomas.length === 0 &&
    certs.length === 0
  ) {
    return null;
  }

  const ContactRow = ({ icon: Icon, value }: { icon: any; value?: string }) =>
    value ? (
      <div className="flex items-center gap-2 text-sm">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-foreground truncate">{value}</span>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Estrutura detectada no currículo</h3>
      </div>

      {(hasContato || contato.nome) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              {contato.nome || "Contato"}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-0">
            <ContactRow icon={Mail} value={contato.email} />
            <ContactRow icon={Phone} value={contato.telefone} />
            <ContactRow icon={MapPin} value={contato.localizacao} />
            <ContactRow icon={Linkedin} value={contato.linkedin} />
          </CardContent>
        </Card>
      )}

      {data.resumo_profissional && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Resumo Profissional</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-muted-foreground whitespace-pre-line">{data.resumo_profissional}</p>
          </CardContent>
        </Card>
      )}

      {experiencias.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              Experiências ({experiencias.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {experiencias.map((exp, i) => (
              <div key={i} className="border-l-2 border-primary/30 pl-3">
                <div className="font-medium text-sm text-foreground">{exp.cargo || "—"}</div>
                <div className="text-xs text-muted-foreground">
                  {[exp.empresa, exp.periodo].filter(Boolean).join(" · ")}
                </div>
                {exp.destaques && exp.destaques.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {exp.destaques.map((d, j) => (
                      <li key={j} className="text-xs text-muted-foreground flex gap-1.5">
                        <span className="text-primary shrink-0">•</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {formacao.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" />
              Formação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pt-0">
            {formacao.map((f, i) => (
              <div key={i} className="text-sm">
                <span className="font-medium text-foreground">{f.curso || "—"}</span>
                <span className="text-muted-foreground">
                  {" "}{[f.instituicao, f.periodo].filter(Boolean).join(" · ")}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {(skills.length > 0 || idiomas.length > 0 || certs.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {skills.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-primary" />
                  Habilidades técnicas
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1.5 pt-0">
                {skills.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                ))}
              </CardContent>
            </Card>
          )}
          {idiomas.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Languages className="h-4 w-4 text-primary" />
                  Idiomas
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1.5 pt-0">
                {idiomas.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                ))}
              </CardContent>
            </Card>
          )}
          {certs.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Award className="h-4 w-4 text-primary" />
                  Certificações
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-1.5 pt-0">
                {certs.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export function ResumeAnalyzerView({ onBack }: ResumeAnalyzerViewProps) {

  const [curriculo, setCurriculo] = useState("");
  const [vaga, setVaga] = useState("");
  const [viewState, setViewState] = useState<ViewState>("idle");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [mode, setMode] = useState<AnalysisMode>("quality");
  const [previousResult, setPreviousResult] = useState<AnalysisResult | null>(null);
  const [uploadedFile, setUploadedFile] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Limite: 5MB");
      return;
    }

    setIsExtracting(true);
    try {
      const text = await extractTextFromFile(file);
      if (!text.trim()) {
        toast.error("Não foi possível extrair texto do arquivo.");
        return;
      }
      setCurriculo(text);
      setUploadedFile(file.name);
      toast.success("Texto extraído com sucesso!");
    } catch (err: any) {
      toast.error(err.message || "Erro ao processar arquivo.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    e.target.value = "";
  };

  const removeFile = () => {
    setUploadedFile(null);
    setCurriculo("");
  };

  const handleAnalyze = async () => {
    const cvText = curriculo.trim();
    const jobText = vaga.trim();

    if (cvText.length < 200) {
      toast.error("Currículo muito curto. Envie ao menos 200 caracteres para uma análise útil.");
      return;
    }
    if (mode === "comparison" && jobText.length < 100) {
      toast.error("Descrição da vaga muito curta. Cole ao menos 100 caracteres.");
      return;
    }

    setViewState("loading");

    try {
      const body: any = { curriculo: cvText };
      if (mode === "comparison") {
        body.vaga = jobText;
      }

      const { data, error } = await supabase.functions.invoke("analyze-resume", { body });

      if (error) {
        const status = (error as any).context?.status;
        if (status === 429) throw new Error("Muitas requisições. Aguarde alguns segundos e tente novamente.");
        if (status === 402) throw new Error("Créditos da IA esgotados. Contate o suporte.");
        throw error;
      }
      if (data?.error) throw new Error(data.error);

      setResult(data as AnalysisResult);
      setViewState("result");
    } catch (err: any) {
      console.error("Erro na análise:", err);
      toast.error(err?.message || "Erro ao analisar currículo. Tente novamente.");
      setViewState("idle");
    }
  };

  const handleReanalyze = () => {
    if (result) {
      setPreviousResult(result);
    }
    setResult(null);
    setCurriculo("");
    setUploadedFile(null);
    setViewState("idle");
  };

  const handleReset = () => {
    setViewState("idle");
    setResult(null);
    setPreviousResult(null);
    setCurriculo("");
    setVaga("");
    setUploadedFile(null);
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return "text-emerald-500";
    if (score >= 40) return "text-amber-500";
    return "text-red-500";
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return "bg-emerald-500/10 border-emerald-500/30";
    if (score >= 40) return "bg-amber-500/10 border-amber-500/30";
    return "bg-red-500/10 border-red-500/30";
  };

  const getNivelBadge = (nivel: string) => {
    switch (nivel) {
      case "Alto":
        return "bg-emerald-500/20 text-emerald-700 border-emerald-500/30";
      case "Médio":
        return "bg-amber-500/20 text-amber-700 border-amber-500/30";
      default:
        return "bg-red-500/20 text-red-700 border-red-500/30";
    }
  };

  const badgeLabel = mode === "quality" ? "Qualidade" : "Compatibilidade";

  if (viewState === "loading") {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Voltar ao Academy
        </Button>
        <div className="flex flex-col items-center justify-center py-24 gap-6">
          <div className="relative">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
              <FileSearch className="h-10 w-10 text-primary" />
            </div>
            <Loader2 className="h-8 w-8 text-primary animate-spin absolute -bottom-1 -right-1" />
          </div>
          <div className="text-center space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Analisando seu currículo...</h3>
            <p className="text-sm text-muted-foreground max-w-sm">
              Nossa IA está {mode === "quality" ? "avaliando a qualidade do seu currículo" : "comparando suas qualificações com os requisitos da vaga"}. Isso pode levar alguns segundos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (viewState === "result" && result) {
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Voltar ao Academy
        </Button>

        <div className="space-y-6">
          <Card className={`border ${getScoreBg(result.score)}`}>
            <CardContent className="py-8 flex flex-col items-center gap-3">
              <div className="flex items-center gap-3">
                <Target className={`h-8 w-8 ${getScoreColor(result.score)}`} />
                <span className={`text-6xl font-bold ${getScoreColor(result.score)}`}>
                  {result.score}
                </span>
                <span className="text-2xl text-muted-foreground font-medium">/100</span>
              </div>
              <Badge variant="outline" className={getNivelBadge(result.nivel)}>
                {badgeLabel} {result.nivel}
              </Badge>
              {result.score >= 80 && result.lacunas.length === 0 && (
                <div className="flex items-center gap-2 mt-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">
                    Seu currículo está ótimo — pronto para envio!
                  </span>
                </div>
              )}
              <p className="text-sm text-muted-foreground text-center max-w-lg mt-2">
                {result.resumo}
              </p>
            </CardContent>
          </Card>

          {result.estrutura && <StructuredResumePanel data={result.estrutura} />}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Pontos Fortes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2">
                  {result.pontos_fortes.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-emerald-500 shrink-0 mt-0.5">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  Lacunas
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2">
                  {result.lacunas.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-amber-500 shrink-0 mt-0.5">⚠</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-600">
                  <Lightbulb className="h-4 w-4" />
                  Sugestões
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2">
                  {result.sugestoes.map((item, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex gap-2">
                      <span className="text-blue-500 shrink-0 mt-0.5">💡</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {previousResult && (
            <ComparisonPanel previous={previousResult} current={result} />
          )}

          <div className="flex justify-center gap-3">
            <Button onClick={handleReanalyze} variant="default" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Melhorar e Reanalisar
            </Button>
            <Button onClick={handleReset} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Nova Análise do Zero
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 mb-4 -ml-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />
        Voltar ao Academy
      </Button>

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileSearch className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Analisador de Currículo</h2>
            <p className="text-sm text-muted-foreground">
              Avalie a qualidade do seu currículo ou compare com uma vaga
            </p>
          </div>
        </div>

        <Tabs value={mode} onValueChange={(v) => setMode(v as AnalysisMode)}>
          <TabsList className="w-full max-w-md">
            <TabsTrigger value="quality" className="flex-1 gap-1.5">
              <BarChart3 className="h-4 w-4" />
              Avaliar Qualidade
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex-1 gap-1.5">
              <Target className="h-4 w-4" />
              Comparar com Vaga
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Upload zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-border/60 rounded-lg p-6 text-center hover:border-primary/40 transition-colors"
        >
          {uploadedFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-foreground">{uploadedFile}</span>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={removeFile}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : isExtracting ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Extraindo texto...</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">
                Arraste um arquivo PDF, DOCX ou TXT aqui, ou{" "}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  selecione do computador
                </button>
              </p>
              <p className="text-xs text-muted-foreground/70">PDF · DOCX · TXT · até 5MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.docx,.md,.rtf"
            onChange={handleFileInput}
            className="hidden"
          />
        </div>

        <div className={`grid gap-4 ${mode === "comparison" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
          <div className="space-y-2">
            <Label htmlFor="curriculo" className="text-sm font-medium">
              Seu Currículo
            </Label>
            <Textarea
              id="curriculo"
              placeholder="Cole aqui o conteúdo do seu currículo ou faça upload acima..."
              value={curriculo}
              onChange={(e) => {
                setCurriculo(e.target.value);
                if (uploadedFile) setUploadedFile(null);
              }}
              className="min-h-[280px] resize-none"
            />
            <p className="text-xs text-muted-foreground">{curriculo.length}/15.000 caracteres</p>
          </div>

          {mode === "comparison" && (
            <div className="space-y-2">
              <Label htmlFor="vaga" className="text-sm font-medium">
                Descrição da Vaga
              </Label>
              <Textarea
                id="vaga"
                placeholder="Cole aqui a descrição da vaga desejada..."
                value={vaga}
                onChange={(e) => setVaga(e.target.value)}
                className="min-h-[280px] resize-none"
              />
              <p className="text-xs text-muted-foreground">{vaga.length}/10.000 caracteres</p>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <Button
            onClick={handleAnalyze}
            disabled={!curriculo.trim() || (mode === "comparison" && !vaga.trim())}
            size="lg"
            className="gap-2"
          >
            <FileSearch className="h-5 w-5" />
            {mode === "quality" ? "Avaliar Qualidade" : "Comparar com Vaga"}
          </Button>
        </div>
      </div>
    </div>
  );
}
